import { ReactNode, useEffect, useState } from 'react'
import { Link, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { Sidebar } from './Sidebar'
import { Login } from '../pages/Login'
import { getBrand, applyBrandToDocument } from '../lib/brand'
import { DateWindowProvider, DateWindowPicker, useCreativeV2 } from '../lib/creativeV2'
import { CreativeDrawerProvider } from './CreativeDrawer'
import { CompetitorDrawerProvider } from './creative/CompetitorDrawer'

// Shared with vanilla auth.js (localStorage key '9fig-theme') so toggling in
// React persists when the user opens a vanilla page in the same browser.
const THEME_KEY = '9fig-theme'

// Replicates the body grid layout from app/*.html: 60px header + 240px sidebar
// + main stage. Theme attribute picked up by shared.css.
export function AppShell({ children }: { children: ReactNode }) {
  const { user, me, loading, signOut, activeTenant } = useAuth()
  const location = useLocation()
  const creativeV2 = useCreativeV2()
  const brand = getBrand()

  useEffect(() => {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved) document.body.setAttribute('data-theme', saved)
    applyBrandToDocument(brand)
  }, [brand])

  const toggleTheme = () => {
    const cur = document.body.getAttribute('data-theme')
    const next = cur === 'neural' ? '' : 'neural'
    document.body.setAttribute('data-theme', next)
    localStorage.setItem(THEME_KEY, next)
  }

  // Loading / signed-out states need to escape the body grid layout
  // (240px sidebar + 1fr) which would otherwise stuff a single child into
  // the top-left cell. position:fixed + inset:0 puts these screens above
  // the grid entirely.
  if (loading) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-app)', color: 'var(--text-muted)', fontSize: 14,
      }}>
        Loading…
      </div>
    )
  }
  if (!user) return <Login />

  // Vanilla login flow (login.html:473): super admins are routed to
  // /admin on first sign-in to explicitly pick a tenant. Without an
  // active tenant in sessionStorage, every /api/* call returns 401 (server
  // requires X-Tenant-ID header). Mirror that here: route to /admin
  // unless they're already there, or on a static, tenant-agnostic page
  // that makes no /api/* calls (e.g. ExperimentGuide, opened target="_blank"
  // — new tabs don't reliably inherit sessionStorage, so activeTenant reads
  // empty there even for a super admin who already picked a tenant).
  const TENANT_EXEMPT_PATHS = ['/admin', '/admin.html', '/experiment-guide', '/experiment-guide.html']
  if (me?.is_super_admin && !activeTenant && !TENANT_EXEMPT_PATHS.includes(location.pathname)) {
    return <Navigate to="/admin" replace />
  }

  // Super-admin who hasn't picked a tenant yet is on the tenant-picker (/admin).
  // There's no tenant context, so the sidebar nav is dead weight — hide it and
  // let the picker use the full width.
  const pickingTenant = !!me?.is_super_admin && !activeTenant

  return (
    <DateWindowProvider><CreativeDrawerProvider><CompetitorDrawerProvider>
      <header className="app-header">
        <a href="/" className="brand-logo">
          {brand.logoAspect === 'wide' ? (
            <img src={brand.logo} alt={brand.name} style={{ height: 32, width: 'auto' }} />
          ) : (
            <>
              <img src={brand.logo} alt={brand.name} width={32} height={32} />
              <span>{brand.name}</span>
            </>
          )}
        </a>
        <div className="control-deck" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {creativeV2 && !pickingTenant && <DateWindowPicker />}
          <TenantBadge />
          <button onClick={toggleTheme} style={{ background: 'transparent', border: '1px solid var(--border)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', color: 'var(--text-main)', fontSize: 11 }}>Theme</button>
          {me?.is_super_admin && (
            <Link to="/admin"
                  style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700,
                           textDecoration: 'none', padding: '6px 12px',
                           border: '1px solid var(--accent)', borderRadius: 6 }}>
              Admin
            </Link>
          )}
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{me?.email}</span>
          <button onClick={signOut} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>Sign out</button>
        </div>
      </header>
      {!pickingTenant && <Sidebar />}
      <main className="main-stage" style={pickingTenant ? { gridColumn: '1 / -1' } : undefined}>{children}</main>
    </CompetitorDrawerProvider></CreativeDrawerProvider></DateWindowProvider>
  )
}

// Tenant logo (with fallback to display name) — read-only in the header.
// Switching happens on /admin → Switch Tenant tab.
function TenantBadge() {
  const { me, activeTenant } = useAuth()
  const tenant = activeTenant || me?.tenant_id
  const [errored, setErrored] = useState(false)

  // Reset error state when tenant changes (different file might exist).
  useEffect(() => { setErrored(false) }, [tenant])

  if (!tenant) return null

  // Known logo extensions per tenant (matches vanilla js/auth.js convention).
  const logoExt: Record<string, string> = {
    supportpets: 'png',
    '4patriots': 'jpeg',
    consumeraffairs: 'png',
    warmies: 'png',
  }
  const displayName = me?.display_name || tenant

  if (errored) {
    return (
      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
        {displayName}
      </span>
    )
  }
  return (
    <img
      src={`/tenant-logo/${tenant}.${logoExt[tenant] || 'png'}?v=2`}
      alt={displayName}
      title={displayName}
      // Match vanilla js/auth.js: 42px height, generous max-width so wide
      // wordmark logos (e.g. ConsumerAffairs at ~400×50) render legibly
      // alongside square-ish logos (SupportPets, 4Patriots).
      style={{ height: 42, maxWidth: 400, objectFit: 'contain' }}
      onError={() => setErrored(true)}
    />
  )
}
